export type CaseStudyImage = {
  src: string;
  alt: string;
};

export type CaseStudySection = {
  /** Short label shown in the left column (e.g. Problem, Challenge). */
  label?: string;
  /** Optional bold lead line above the body text. */
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
  /** Optional captioned images shown full width, in order, at the end of the section. */
  figures?: CaseStudyFigure[];
};

/** A single full-width image, or a row of images (e.g. phone screens) sharing one caption. */
export type CaseStudyFigure =
  | { src: string; alt: string; caption: string }
  | { images: CaseStudyImage[]; caption: string };

export const amexIntro = {
  client: 'American Express',
  title: 'Cutting enterprise card origination from 22 to 18 days',
  summary:
    'Redesigned onboarding for multi-million dollar corporate spend accounts, cutting sales cycle time 18%.',
  background: 'linear-gradient(180deg, #DAF0FC 0%, #A3BECD 100%)',
};

export const amexMetrics = [
  { value: '18%', label: 'faster sales cycle' },
  { value: '89%', label: 'better accessibility' },
  { value: '$25M+', label: 'typical client revenue' },
];

export const amexImages: CaseStudyImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/uuZLboQhqmjxRXsArBMAVr/01_Company_Info.jpg',
    alt: 'Step 1: Company Information, with legal, incorporation, demographic, and headquarters details',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/nTiK6HSCEsCnYohdZGiiyF/02_Authorized_Officer.jpg',
    alt: 'Step 2: Authorized Officer, with mandatory read-aloud disclosures and credit inquiry consent',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/xgRDofHfvm8wdr1PxFJpvk/03_Product_Details.jpg',
    alt: 'Step 4: Product Details, with product, reward types, and sub product selection',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/2noQv98HjYuAgFnZbK64eV/04_Document_Upload.jpg',
    alt: 'Step 7: Document Upload, with required documents and a drag-and-drop upload area',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/kUFxaJcF8zqkNNE9nJKAMA/05_Approved.jpg',
    alt: 'Application Status: Valley Tech LLC approved, with a read-aloud confirmation for the client',
  },
];

export const amexSections: CaseStudySection[] = [
  {
    label: 'Problem',
    heading: "A form that got in the salespeople's way",
    paragraphs: [
      "Amex's credit application for its largest corporate clients lived inside Salesforce. It was clunky and hard to access, and it pulled salespeople away from relationship building into data entry. Moving it into a standalone application would make their jobs easier and, in turn, improve the client experience.",
    ],
  },
  {
    label: 'Challenge',
    heading: 'A large organization with many opinions',
    paragraphs: ['Amex is a big, matrixed organization, and each group saw the problem differently.'],
  },
  {
    label: 'Key Decisions',
    bullets: [
      'I documented what was already known about the credit decisioning process, so everyone started from the same picture.',
      'I made flexibility the priority for sales users. These applications were often filled in over several weeks, so the tool had to handle changes and revisions throughout.',
    ],
  },
  {
    label: 'Approach',
    heading: "Getting everyone's requirements early",
    paragraphs: [
      'I gathered feedback from sales, marketing, risk, and compliance early and kept doing so throughout the design process. That way, requirements surfaced before they could derail the work.',
    ],
  },
  {
    label: 'Results',
    heading: 'Documentation, prototype, and results',
    paragraphs: [
      'I produced UX documentation of the credit decisioning process and a full Figma prototype of the product, later rebuilt in Magic Patterns. For the highest-value clients, the sales cycle dropped 18%, from 22 days to 18. Accessibility issues fell 89%, from 91 to 10. Users responded well, and adoption was universal.',
    ],
  },
  {
    figures: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/jbKPV9HDu8Mba9QJfTaT8e/01_Company_Info.jpg',
        alt: 'Corporate Card Program Application, step 1 of 7: Company Information form with legal, incorporation, demographic, and headquarters details',
        caption: 'Company information, grouped into clear sections with inline help.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/2dExJQdiBVjmxyPGeLt6hr/02_Authorized_Officer.jpg',
        alt: 'Step 2 of 7: Authorized Officer form with mandatory read-aloud disclosures placed beside the relevant fields',
        caption: 'Mandatory disclosures surfaced right where salespeople need to read them.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/86gw74n6VDTNXLXSBGudtj/03_Product_Details.jpg',
        alt: 'Step 4 of 7: Product Details with product selection, reward types, and sub product',
        caption: 'Product and rewards selection with plain-language explanations.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/tv7i93wow2nTxw1kUXvnX2/04_Document_Upload.jpg',
        alt: 'Step 7 of 7: Document Upload with required documents, a drag-and-drop area, and a list of uploaded files',
        caption: 'Document upload with requirements called out up front.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/hPGVYNU9ZMq5Ynowtgv5ZT/05_Approved.jpg',
        alt: 'Application Status screen showing Valley Tech LLC approved, with a read-aloud message for the client',
        caption: 'Approval confirmation with a script to share with the client.',
      },
    ],
  },
];
